import React, { useEffect, useState, ReactElement, useRef } from 'react'
import { Button, Layout, Space, Table, Input, Avatar, message, Radio, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Highlighter from "react-highlight-words";
import firebase from "firebase/app";
import { SearchOutlined, UserOutlined, DeleteFilled, AreaChartOutlined, PlusOutlined } from "@ant-design/icons";
import { ContentHeader } from "../../components/GlobalStyles";
import { getAuthState, getClubState } from "../../redux/selectors";
import { SET_LOADING_STATE, RESET_LOADING_STATE } from "../../redux/constants";

const { Content } = Layout;

export function UserTable(): ReactElement {
  const [users, setUsers] = useState<any[]>([]);
  const [club, setClub] = useState<any>(null);
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>();

  const { currentUser } = useSelector(getAuthState);
  const dispatch = useDispatch();

  const getUsers = async () => {
    if (currentUser?.role !== "manager") return;
    const clubId = currentUser?.club;
    if (clubId === "" || clubId === null) return;
    dispatch({ type: SET_LOADING_STATE });
    const clubSnapshot = await firebase.firestore().collection("clubs").doc(clubId).get();
    const currentClub = clubSnapshot.data();
    setClub(currentClub);
    const bookingSnapshots = await firebase.firestore().collection("bookings").where("club", "==", clubId).get();
    let tempUserIds = bookingSnapshots.docs.map(doc => doc.data().userId);
    tempUserIds = tempUserIds.filter(id => id !== "");
    const userIds: string[] = [];
    tempUserIds.forEach(id => {
      if (userIds.findIndex(uid => uid === id) === -1) userIds.push(id);
    })
    const mapUsers = await Promise.all(userIds.map(async uid => {
      const userSnapshot = await firebase.firestore().collection("users").doc(uid).get();
      const userData = userSnapshot.data();
      const priorityList = currentClub?.priority ?? [];
      const isPriority = priorityList.findIndex((p: string) => p === uid) > -1;
      return {
        ...userData,
        isPriority
      };
    }));
    const clubUsers = mapUsers.filter(u => u !== null);
    setUsers(clubUsers);
    dispatch({ type: RESET_LOADING_STATE });
  }

  useEffect(() => {
    getUsers();
  }, []);

  const onChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    // eslint-disable-next-line no-console
    console.log("params", pagination, filters, sorter, extra);
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: any,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText("");
  };


  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: (props: any) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props;
      return (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: string, record: any) => {
      if (record[dataIndex])
        return record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      return "";
    },
    onFilterDropdownVisibleChange: (visible: boolean) => {
      // if (visible) {
      //   setTimeout(() => searchInput.current?, 100);
      // }
      console.log(searchInput.current);
    },
    render: (text: string) => {
      if (searchedColumn === dataIndex) {
        if (dataIndex === "email" || dataIndex === "phoneNumber") {
          return (
            <Button type="link" href={dataIndex === "email" ? `mailto:${text}` : `tel:${text}`}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ""}
              />
            </Button>
          );
        }
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        );
      }
      if (dataIndex === "email" || dataIndex === "phoneNumber") {
        return (
          <Button type="link" href={dataIndex === "email" ? `mailto:${text}` : `tel:${text}`}>
            {text}
          </Button>
        )
      }
      return text;
    },
  });

  const columns: any = [
    {
      title: "Photo",
      key: "photoUrl",
      render: (record: any) => (
        <Avatar src={record.photoUrl} size={30} icon={<UserOutlined />} />
      ),
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      ...getColumnSearchProps("phoneNumber"),
    },
    {
      title: "Gender",
      key: "gender",
      render: (record: any) => (
        <td>
          <span style={{ textTransform: "capitalize" }}>{record.gender}</span>
        </td>
      )
    },
    {
      title: "Instagram",
      dataIndex: "instagram",
      key: "instagram",
    },
    {
      title: "Priority",
      key: "priority",
      render: (record: any) => {
        if (!record.isPriority) return null;
        return (
          <Space style={{ width: 200, flexWrap: "wrap" }}>
            <Tag color="#E02401">Priority</Tag>
          </Space>
        )
      }
    },
  ];

  return (
    <Content>
      <ContentHeader>Users</ContentHeader>
      <Table columns={columns} dataSource={users} onChange={onChange} pagination={false} />
    </Content>
  )
}

